import { Avatar, Box, FormControlLabel, Popover, Radio, RadioGroup, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import { ICharacterData, INoteData, IParagraphData, IScriptElement, ScriptElementData } from "../script/parseScript";
import { ProjectDetails } from "../../redux/types/firebase";
import { parseScript } from "../script/parseScript";
import {IScript, IStateComment} from "./Writer";
import { selectProject, selectSearchText } from "../../redux/selectors/editor";
import { connect } from "react-redux";
import GrammarPopup from "./GrammarPopup";
import { httpsCallable, getFunctions } from "firebase/functions";
import { IGrammarRequest, IGrammarResult, IGrammarRule } from "./EditState";
import ScriptRender, { IValues, ScriptTypeRenderers } from "./ScriptRender";
import { ScriptTextRender } from "./ScriptTextRenderer";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {selectDictionary} from "../../redux/selectors/firebase";
import {Phone} from "@mui/icons-material";

function replaceText(text: string, offset: number, length: number, replacement: string): string
{
    return text.slice(0, offset) + replacement + text.slice(offset + length);
}

const useScriptStyles = makeStyles(theme => ({
    avatar: {
        height: theme.spacing(8),
        width: theme.spacing(8),
    },
    character: {
        fontFamily: 'inherit',
        fontWeight: "bold",
        textAlign: "center",
    },
    cut: {
        fontFamily: 'inherit',
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    gesture: {
        fontFamily: 'inherit',
    },
    heading: {
        fontFamily: 'inherit',
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    line: {
        fontFamily: 'inherit',
    },
    note: {
        marginTop: 12,
        marginBottom: 12,
    },
    option: {
        fontFamily: 'inherit',
    },
    state: {
        fontFamily: 'courier',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(12),
        marginRight: theme.spacing(12),
        padding: theme.spacing(1),
        borderRadius: "6px",
        borderStyle: "solid",
        borderColor: "transparent",
        borderWidth: "1px"
    },
    highlighted: {
        backgroundColor: "rgba(1,1,1,0.04)"
    },
    title: {
        fontFamily: "inherit",
        fontSize: "large",
        fontWeight: "bold",
        marginBottom: 12,
        textAlign: "center",
    },
    textHighlight: {
        background: "yellow",
        position: "absolute",
        zIndex: -1
    },
    comment: {
        backgroundColor: theme.palette.comment.main
    },
    commentHighlight: {
        backgroundColor: theme.palette.commentHighlight.main
    },
    grammar: {
        textDecorationColor: theme.palette.grammar.main,
        textDecorationLine: "underline",
        textDecorationStyle: "double",
        textDecorationWidth: 1
    },
    search: {
        backgroundColor: theme.palette.search.main
    },
    spelling: {
        textDecorationColor: theme.palette.spelling.main,
        textDecorationLine: "underline",
        textDecorationStyle: "double",
        textDecorationWidth: 1
    }
}));
export interface IScriptTag
{
    tooltip: string
    suggestions?: IValues[]
    offset: number
    length: number
    rule: string
    context: string
}
type Choices = { [key: string]: string };
type ActiveTags = {
    comment?: IScriptTag
    commentHighlight?: IScriptTag
    grammar?: IScriptTag
    search?: IScriptTag
    spelling?: IScriptTag
};

interface IProps
{
    choices: Choices
    comments: IStateComment[]
    dictionary: {[word: string]: any}
    index: number
    onUpdateGrammar: (state: string, text: string, grammar: IGrammarRule[]) => void
    onChoice: (state: string, choice: string) => void
    onEdit: (state: string | undefined) => void
    onSetHeight: (index: number, height: number) => void
    onStateHighlight: (stateId?: string) => void
    updateScript: (index: number, state: string, script: string) => void
    setSelectionRange: (state: string, start: number, end: number) => void
    projectDetails: ProjectDetails
    project: string
    script: IScript
    searchText: string
    state: string
    commentHighlight?: string
    highlight: boolean
}
function State({commentHighlight, comments, dictionary, highlight, choices, index, onChoice, onSetHeight, onEdit,
                   onStateHighlight, onUpdateGrammar, updateScript, setSelectionRange, project, projectDetails, script, searchText,
                   state} : IProps)
{
    const classes = useScriptStyles();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [activeTag, setActiveTag] = useState<ActiveTags | null>(null);

    const parsedScript = useMemo(()=>parseScript(script.text), [script.text]);

    const updateGrammarRef = useRef(httpsCallable<IGrammarRequest, IGrammarResult>(
        getFunctions(undefined, "europe-west2"), "updateGrammar"));

    const handleLineClick = () =>
    {
        handleSelectionChange();
        const selection = document.getSelection();
        if(selection){
            const range = selection.getRangeAt(0);
            if(range && range.startContainer === range.endContainer && range.startOffset === range.endOffset)
            {
                onEdit(state);
            }

            return;
        }

        onEdit(state);        
    }
    const sizeRef = useRef<HTMLDivElement>(null);
    const handleChoiceChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        event.stopPropagation();
        onChoice(state, event.target.value);
    };
    const handleChoiceClick = (event: React.MouseEvent<HTMLInputElement>) =>
    {
        event.stopPropagation();
    };
    const handleGrammarSpellingClick = (activeTags: ActiveTags) =>
    {
        const tags = {
            ...activeTags
        }
        return activeTags.grammar !== undefined || activeTags.spelling !== undefined ? (event: React.MouseEvent<HTMLElement>) =>
        {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
            setActiveTag(tags);
        } : undefined
    }

    const handleCloseGrammarSpelling = (event: MouseEvent) =>
    {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const renderOption = (element: IScriptElement, key: string, index: number, activeTags: ActiveTags, textRender: ScriptTextRender) =>
    {
        const options = element.data as ScriptElementData[];
        const keyText = `${key}_${index}`;

        return (
            <Box key={keyText} display="flex" flexDirection="column">
                <RadioGroup value={choices[key]} data-state={key} onChange={handleChoiceChange} onClick={handleChoiceClick}>
                    {options.map((option, index) => (
                        <FormControlLabel classes={{ label: classes.option }} key={index}
                            control={<Radio />} label={
                                textRender.renderAnnotated(option.condition ? `${option.text} (if ${option.condition})` : option.text,
                                    index, textRender.getStyleTags(option.text, textRender.tagOffset), activeTags)
                            } value={option.text} />
                    ))}
                </RadioGroup>
            </Box>
        );
    };
    const renderers: ScriptTypeRenderers = {
        character: (element, key, index, styles, textRender) =>
        {
            const data = element.data as ICharacterData;
            const image = `${data.character.toLowerCase()}.png`;
            const icons: { [key: string]: ReactElement } = {
                phone: <Phone />,
                voicemail: <Phone />
            };
            const icon = icons[data.character.toLowerCase()];
            let scriptOffset = data.offset;
            const lines = data.lines.map((line, index) =>
            {
                const tags = textRender.getStyleTags(line, scriptOffset);

                scriptOffset += line.length + 1 // For the new line;
                return textRender.renderLine((line || '\u00A0') as string, index, tags, styles);
            });

            return (
                <Box key={`${key}_${index}`} display="flex" flexDirection="row">
                    <Box display="flex" flexDirection="column" minWidth={200}>
                        {icon ?
                            <Avatar className={classes.avatar}>{icon}</Avatar> :
                            <Avatar
                                className={classes.avatar}
                                src={`${projectDetails.imageSource}/intertitle/characters/${image}`} />
                        }
                    </Box>
                    <Box flexGrow={1} display="flex" flexDirection="column">
                        <Typography className={classes.character}>{data.character}</Typography>
                        {data.parenthetical &&
                            <Typography className={classes.character}>
                                ({data.parenthetical})
                            </Typography>}
                        {lines}
                    </Box>
                </Box>
            );
        },
        comment: (element: IScriptElement, key: string, index: number, styles, textRender) =>
            null,
        cut: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const data = element.data as ScriptElementData;

            return (
                <Box key={`${key}_${index}`}>
                    {textRender.renderLine(data.text, 0, textRender.getStyleTags(data.text, 0), styles)}
                </Box>
            );
        },
        gesture: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const gestures = element.data as ScriptElementData[];

            return (
                <Box key={`${key}_${index}`} display="flex" flexDirection="column">
                    <RadioGroup value={choices[key]} data-state={key} onChange={handleChoiceChange} onClick={handleChoiceClick}>
                        {gestures.map((gesture, index) => (
                            <FormControlLabel classes={{ label: classes.gesture }} key={index} control={
                                <Radio onClick={event => event.stopPropagation()} />
                            } label={
                                textRender.renderLine(gesture.text, index, textRender.getStyleTags(gesture.text, textRender.tagOffset), styles)
                            } value={gesture.text} />
                        ))}
                    </RadioGroup>
                </Box>
            );
        },
        paragraph: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const data = element.data as IParagraphData;
            const firstLine = data.lines.find(line => line.length > 0);
            let scriptOffset = script.text.indexOf(firstLine || "");
            const lines = data.lines.map((line, index) =>
            {
                const tags = line === "" ? [] : textRender.getStyleTags(line, scriptOffset);

                scriptOffset += line.length + 1 // new line is a character;
                return textRender.renderLine((line || '\u00A0') as string, index, tags, styles);
            });

            return (
                <Box key={`${key}_${index}`}>
                    {lines}
                </Box>
            );
        },
        note: (element: IScriptElement, key: string, index: number, styles, textRender) =>
        {
            const data = element.data as INoteData;

            return (
                <Box className={classes.note} key={`${key}_${index}`} fontWeight="bold">
                    {textRender.renderAnnotated(`(${data.note}) - `, 0, textRender.getStyleTags(data.line, textRender.tagOffset), styles)}
                    {textRender.renderAnnotated(data.line, data.note.length, textRender.getStyleTags(data.line, 0), styles)}
                </Box>
            );
        },
        conditional: renderOption,
        option: renderOption,
        still: (element, key, index) =>
        {
            const data = element.data as ScriptElementData;
            const url = encodeURI(`${projectDetails.imageSource}/stills/${data.text}`);

            return (
                <Box key={`${key}_${index}`} display="flex" flexDirection="column">
                    <Box flexGrow={1} style={{
                        backgroundImage: `url(${url})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        height: 100
                    }} />
                </Box>
            );
        },
        title: (element, key, index, styles, textRender) =>
        {
            const data = element.data as ScriptElementData;

            return (
                <Box key={`${key}_${index}`}>
                    {textRender.renderLine(data.text, index, textRender.getStyleTags(data.text, 0), styles)}
                </Box >
            );
        },
    };

    const content = <ScriptRender content={parsedScript}
        classes={classes}
        state={state}
        renderers={renderers}
        onClickFragment={handleGrammarSpellingClick} text={script.text}
        highlightOptions={{
            comments,
            commentHighlight,
            dictionary: Object.keys(dictionary || {}),
            grammar: script.grammar,
            ignoredGrammar: script.ignoredGrammar,
            searchText
        }} />

    useEffect(() =>
    {
        if (sizeRef.current)
        {
            const style = window.getComputedStyle(sizeRef.current);
            const elementHeight = sizeRef.current.offsetHeight + parseInt(style.marginTop)

            if (sizeRef.current.children.length > 0)
            {
                onSetHeight(index, elementHeight);
            }
        }
    }, [index, onSetHeight]);

    const handleSelectionChange = () =>
    {
        const selection = window.getSelection();

        if (selection && highlight !== undefined)
        {
            if (selection.rangeCount > 0)
            {
                const range = selection.getRangeAt(0);

                if (range)
                {
                    const startText = range.startContainer.textContent;
                    const endText = range.endContainer.textContent !== "\u00A0" ? range.endContainer.textContent : "\n"
                    const start = startText ? script.text.indexOf(startText) + range.startOffset : 0;
                    const end = endText ? script.text.indexOf(endText) + range.endOffset : script.text.length;
                
                    setSelectionRange(state, start, end);
                }
            }
        }
    }

    const tag = activeTag?.spelling || activeTag?.grammar
    const open = anchorEl !== null;

    const updateGrammar = useCallback((text: string) =>
    {
        updateGrammarRef.current({
            project,
            state,
            text
        }).then((result) =>
        {
            onUpdateGrammar(state, text, result.data.matches);
        }).catch(console.error);
    }, [onUpdateGrammar, project, state]);

    const handleClickSuggestion = useCallback((replacement: string) =>
    {
        const replaced = replaceText(script.text, tag?.offset ?? 0, tag?.length ?? 0, replacement);
        updateScript(index, state, replaced);
        updateGrammar(replaced);
    }, [script.text, updateGrammar, updateScript, index, state, tag?.length, tag?.offset]);
    const handleMouseMove = () => onStateHighlight(state);
    const {pushIgnoreGrammar} = useContext(FirebaseContext);

    const handleClickIgnore = () =>
    {
        if (tag)
        {
            pushIgnoreGrammar(project, state, {
                offset: tag.offset,
                length: tag.length,
                text: script.text.slice(tag.offset, tag.offset + tag.length),
                rule: tag.rule,
                context: tag.context
            });
        }
    }
    const classNames = [classes.state];

    if (highlight) {
        classNames.push(classes.highlighted);
    }
    return (
        <div ref={sizeRef} data-state="stateKey" className={classNames.join(" ")} onClick={handleLineClick}
             onMouseMove={handleMouseMove}>
            {content}
            {tag && <Popover
                open={open} anchorEl={anchorEl} onClose={handleCloseGrammarSpelling}
                anchorOrigin={{ vertical: -30, horizontal: "left"}}>
                <GrammarPopup
                    grammar={tag} onClickSuggestion={handleClickSuggestion} onClose={() => setAnchorEl(null)}
                    onIgnore={handleClickIgnore} project={project} text={script.text}/>
            </Popover>}
        </div>
    );
}

const mapStateToProps = (store: never) => ({
    dictionary: selectDictionary(store),
    searchText: selectSearchText(store),
    project: selectProject(store)
});

export default connect(mapStateToProps, {
})(State);